import CameraFilled from '@ant-design/icons/CameraFilled'
import { CheckIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons'
import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import { styled } from 'styled-components'
import { GeckoMarkupIcon, GeckoMarkupIconID, GeckoMarkupLayer } from 'types'

import { PrimitiveHtmlOverlay3D } from './util/PrimitiveHtmlOverlay3D'
import { useMarkupLayerProperties } from './util/useMarkupLayerProperties'

const StyledIcon = styled(motion.div)<{
  $useThickOutline: boolean
  $useLargeIcon: boolean
  $color: string
  $outlineColor: string
}>`
  svg {
    height: 100%;
    width: 100%;
  }

  border-radius: 3rem;
  padding: 0.19rem;

  border: ${({ $useThickOutline, $outlineColor }) =>
    $useThickOutline
      ? `4px solid ${$outlineColor}`
      : `2px solid ${$outlineColor}`};
  width: ${({ $useThickOutline }) => ($useThickOutline ? `28px` : `24px`)};
  transform: ${({ $useLargeIcon }) => ($useLargeIcon ? `scale(1.3)` : ``)};
  align-items: center;
  justify-content: center;
  display: flex;
  background-color: ${({ $color }) => $color};
`

/**
 * Renders an icon markup primitive on the surface of a component, mapping via
 * its surface's UVs
 * @param markupLayer       The layer this primitive belongs to
 * @param primitive         The primitive being rendered
 * @constructor
 */
export const IconPrimitive3D: React.FC<{
  markupLayer: GeckoMarkupLayer
  primitive: GeckoMarkupIcon
}> = ({ markupLayer, primitive }) => {
  //----------------------------------------------------------------------------

  const { color, outlineColor, isSelected, isHovered } =
    useMarkupLayerProperties({
      markupLayer,
      primitive,
    })

  //----------------------------------------------------------------------------

  return (
    <PrimitiveHtmlOverlay3D
      markupLayer={markupLayer}
      primitive={primitive}
      coords={primitive.coords}>
      <AnimatePresence>
        <StyledIcon
          transition={{ type: 'tween', stiffness: 400, damping: 19 }}
          $useThickOutline={isHovered}
          $useLargeIcon={isSelected}
          $color={color}
          $outlineColor={outlineColor}>
          {primitive.icon_id === GeckoMarkupIconID.Camera ? (
            <CameraFilled style={{ color: outlineColor }} />
          ) : primitive.icon_id === GeckoMarkupIconID.Checkmark ? (
            <CheckIcon
              color={outlineColor}
              style={{ transform: 'scale(1.25)' }}
            />
          ) : primitive.icon_id === GeckoMarkupIconID.ExclamationMark ? (
            <ExclamationTriangleIcon
              color={outlineColor}
              style={{ transform: 'scale(1.2)' }}
            />
          ) : null}
        </StyledIcon>
      </AnimatePresence>
    </PrimitiveHtmlOverlay3D>
  )
}
